<script setup>
import { computed } from 'vue';

const props = defineProps({
  id: {
    type: String,
    default: 'form-button'
  },
  type: {
    type: String,
    default: 'submit',
  },
  label: {
    type: String,
  },
  size: {
    type: String,
    default: 'md',
    validator: (size) => ['xs', 'md'].includes(size)
  },
  buttonTheme: {
    type: String,
    default: 'light',
    validator: (theme) => ['light', 'dark'].includes(theme)
  },
  disabled: {
    type: Boolean,
    default: false
  },
  loading: {
    type: Boolean,
    default: false
  }
});

const emit = defineEmits(['click']);

const buttonClasses = computed(() => ({
  'w-full bg-transparent flex justify-center items-center font-semibold border rounded-full focus:outline-hidden': true,

  'text-xs p-2': props.size === 'xs',
  'text-base px-4 py-2': props.size === 'md',

  'cursor-not-allowed': props.disabled || props.loading,
  'cursor-pointer': !props.disabled && !props.loading,

  'text-gray-400': props.disabled,
  'text-gray-900 hover:text-white': !props.disabled && props.buttonTheme === 'dark',
  'text-white hover:text-black': !props.disabled && props.buttonTheme === 'light',

  'border-gray-400': props.disabled,
  'border-gray-900 hover:border-white': !props.disabled && props.buttonTheme === 'dark',
  'border-white hover:border-black': !props.disabled && props.buttonTheme === 'light',
}));

function onClick (event) {
  if (props.disabled || props.loading) {
    event.preventDefault();
    return;
  }
  emit('click', event);
}

</script>

<template>
  <button
    :class="buttonClasses"
    :type="type"
    :disabled="disabled"
    @click="onClick"
  >
    <span v-if="loading" class="flex h-4 w-4 items-center mr-2">
      <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 40 40" fill="#fff">
        <circle cx="20" cy="20" r="18" stroke="#fff" stroke-width="4" fill="none" stroke-linecap="round"
          stroke-dasharray="90" stroke-dashoffset="60">
          <animateTransform
            attributeName="transform"
            type="rotate"
            from="0 20 20"
            to="360 20 20"
            dur="1s"
            repeatCount="indefinite" />
        </circle>
      </svg>
    </span>

    <slot>
      <span class="uppercase">{{ label }}</span>
    </slot>
  </button>
</template>
